import { useState } from "react";
import { useTranslate } from "helpers";
import { ContactGalleryPreviewProps } from "./types";
import { PictureGalleryPreview } from "ui/components/Pictures";

const ContactGalleryPreview: React.FC<ContactGalleryPreviewProps> = (props) => {
    const { formValues } = props;
    const { t } = useTranslate();
    const [previewOpen, setPreviewOpen] = useState(false);
    const [selectedPictureId, setSelectedPictureId] = useState<string | null>(null);

    // Ako nema slika, koristimo dummy slike
    const dummyImages = [
        { id: "dummy-1", url: "/media/images/logo.png", name: "" },
        { id: "dummy-2", url: "/media/images/logo.png", name: "" },
        { id: "dummy-3", url: "/media/images/logo.png", name: "" },
        { id: "dummy-4", url: "/media/images/logo.png", name: "" },
        { id: "dummy-5", url: "/media/images/logo.png", name: "" },
        { id: "dummy-6", url: "/media/images/logo.png", name: "" },
    ];

    const isDummy = !formValues.pictures || formValues.pictures.length === 0;
    const pictures = !isDummy
        ? formValues.pictures.map(p => ({ id: p.id, url: p.url, name: p.name ?? "" }))
        : dummyImages;

    const handlePictureClick = (pictureId: string) => {
        if (isDummy) return;
        setSelectedPictureId(pictureId);
        setPreviewOpen(true);
    };

    const handleClosePreview = () => {
        setPreviewOpen(false);
        setSelectedPictureId(null);
    };

    const visiblePictures = pictures.slice(0, 5);
    const remainingCount = pictures.length - 5;

    return (
        <>
            <div
                className="pt-4 px-4 grid gap-2 rounded-lg overflow-hidden mb-3"
                style={{
                    gridTemplateColumns: visiblePictures.length === 1
                        ? "1fr"
                        : visiblePictures.length === 2
                            ? "1fr 1fr"
                            : visiblePictures.length === 3
                                ? "1fr 1fr 1fr"
                                : visiblePictures.length === 4
                                    ? "2fr 2fr 1fr"
                                    : "2fr 2fr 1fr 2fr",
                    gridTemplateRows: visiblePictures.length <= 3
                        ? "280px"
                        : "140px 140px",
                }}
            >
                {visiblePictures.map((picture, index) => {
                    const total = visiblePictures.length;
                    const isLastVisible = index === total - 1;
                    const showOverlay = isLastVisible && remainingCount > 0;

                    const getRadiusClasses = () => {
                        if (total === 1) return "rounded-lg";
                        if (total === 2) {
                            if (index === 0) return "rounded-l-lg";
                            if (index === 1) return "rounded-r-lg";
                        }
                        if (total === 3) {
                            if (index === 0) return "rounded-l-lg";
                            if (index === 2) return "rounded-r-lg";
                        }
                        if (total === 4) {
                            if (index === 0) return "rounded-l-lg";
                            if (index === 2) return "rounded-tr-lg";
                            if (index === 3) return "rounded-br-lg";
                        }
                        if (total >= 5) {
                            if (index === 0) return "rounded-l-lg";
                            if (index === 4) return "rounded-r-lg";
                        }
                        return "";
                    };

                    const getGridPosition = (): React.CSSProperties => {
                        if (total >= 5) {
                            switch (index) {
                                case 0: return { gridColumn: "1", gridRow: "1 / 3" };
                                case 1: return { gridColumn: "2", gridRow: "1 / 3" };
                                case 2: return { gridColumn: "3", gridRow: "1" };
                                case 3: return { gridColumn: "3", gridRow: "2" };
                                case 4: return { gridColumn: "4", gridRow: "1 / 3" };
                            }
                        }
                        if (total === 4) {
                            switch (index) {
                                case 0: return { gridColumn: "1", gridRow: "1 / 3" };
                                case 1: return { gridColumn: "2", gridRow: "1 / 3" };
                                case 2: return { gridColumn: "3", gridRow: "1" };
                                case 3: return { gridColumn: "3", gridRow: "2" };
                            }
                        }
                        return {};
                    };

                    return (
                        <div
                            key={picture.id || index}
                            className={`relative overflow-hidden bg-gray-100 ${!isDummy ? "cursor-pointer" : ""} ${getRadiusClasses()}`}
                            style={getGridPosition()}
                            onClick={() => handlePictureClick(picture.id)}
                        >
                            <img
                                src={picture.url}
                                alt={picture.name || `Slika ${index + 1}`}
                                className="absolute inset-0 w-full h-full object-cover"
                            />
                            {showOverlay && !isDummy && (
                                <div className="absolute inset-0 flex items-end justify-end p-3">
                                    <span className="text-white text-sm font-medium bg-black/20 backdrop-blur-sm px-3 py-1.5 rounded-md">
                                        Prikaži sve fotografije ({pictures.length})
                                    </span>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            <PictureGalleryPreview
                show={previewOpen}
                pictures={pictures}
                initialSelectedId={selectedPictureId}
                onClose={handleClosePreview}
            />
        </>
    );
};

export default ContactGalleryPreview;
